/**
 * usePerformanceOptimization Composable
 * 
 * Implementing Performance Optimization Pattern from PATTERNS_FROM_BASKET_BASE.md
 * for Landing Page Phase 7 following ADR-048
 * 
 * Provides comprehensive performance optimizations for landing page:
 * - Image lazy loading with intersection observer
 * - Resource preloading strategies  
 * - Bundle size monitoring
 * - Critical CSS optimization
 * - Memory leak prevention
 * 
 * Features:
 * - Lazy loading with placeholder management
 * - Resource hints management (preload, prefetch)
 * - Performance metrics collection
 * - Memory usage monitoring
 * - Network-aware optimizations
 */

import { ref, onMounted, onUnmounted, nextTick } from 'vue'

export function usePerformanceOptimization() {
  const lazyImages = ref(new Set())
  const preloadedResources = ref(new Set())
  const performanceMetrics = ref({
    firstContentfulPaint: 0,
    largestContentfulPaint: 0,
    firstInputDelay: 0,
    cumulativeLayoutShift: 0,
    loadTime: 0
  })
  
  let imageObserver = null
  let performanceObserver = null

  /**
   * Initialize image lazy loading with Intersection Observer
   */
  const initLazyLoading = () => {
    if (!('IntersectionObserver' in window)) {
      // Fallback for older browsers - load all images immediately
      loadAllImages()
      return
    }

    const imageObserverOptions = {
      root: null,
      rootMargin: '50px 0px', // Start loading 50px before image enters viewport
      threshold: 0.1
    }

    imageObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target
          loadImage(img)
          imageObserver.unobserve(img)
          lazyImages.value.delete(img)
        }
      })
    }, imageObserverOptions)

    // Find and observe all lazy images
    observeLazyImages()
  }

  /**
   * Find and observe images with lazy loading
   */
  const observeLazyImages = () => {
    const lazyImageSelectors = [
      'img[data-src]',
      'img[loading="lazy"]',
      '.lazy-image'
    ]

    lazyImageSelectors.forEach(selector => {
      const images = document.querySelectorAll(selector)
      images.forEach(img => {
        if (!lazyImages.value.has(img)) {
          lazyImages.value.add(img)
          imageObserver?.observe(img)
        }
      })
    })
  }

  /**
   * Load individual image with error handling
   */
  const loadImage = (img) => {
    const src = img.dataset.src || img.src
    
    if (!src) return

    // Create new image for preloading
    const imageLoader = new Image()
    
    imageLoader.onload = () => {
      // Update src and remove placeholder
      img.src = src
      img.classList.remove('loading', 'skeleton-optimized')
      img.classList.add('loaded')
      
      // Remove data-src to prevent re-loading
      delete img.dataset.src
    }
    
    imageLoader.onerror = () => {
      // Handle error - show fallback or placeholder
      img.classList.remove('loading', 'skeleton-optimized')
      img.classList.add('error')
      
      // Optional: Set fallback image
      const fallback = img.dataset.fallback
      if (fallback) {
        img.src = fallback
      }
    }
    
    // Start loading
    imageLoader.src = src
  }

  /**
   * Fallback function to load all images (older browsers)
   */
  const loadAllImages = () => {
    const allImages = document.querySelectorAll('img[data-src]')
    allImages.forEach(img => loadImage(img))
  }

  /**
   * Preload critical resources
   */
  const preloadCriticalResources = (resources = []) => {
    const criticalResources = [
      // Default critical resources
      { href: '/fonts/inter-var.woff2', as: 'font', type: 'font/woff2', crossorigin: 'anonymous' },
      // User-provided resources
      ...resources
    ]

    criticalResources.forEach(resource => {
      if (preloadedResources.value.has(resource.href)) return

      const link = document.createElement('link')
      link.rel = 'preload'
      link.href = resource.href
      link.as = resource.as
      
      if (resource.type) link.type = resource.type
      if (resource.crossorigin) link.crossOrigin = resource.crossorigin
      
      document.head.appendChild(link)
      preloadedResources.value.add(resource.href)
    })
  }

  /**
   * Prefetch non-critical resources
   */
  const prefetchResources = (resources = []) => {
    // Only prefetch if connection is fast and not on mobile data
    const connection = navigator.connection
    if (connection && (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g')) {
      return // Skip prefetch on slow connections
    }

    resources.forEach(href => {
      if (preloadedResources.value.has(href)) return

      const link = document.createElement('link')
      link.rel = 'prefetch'
      link.href = href
      
      document.head.appendChild(link)
      preloadedResources.value.add(href)
    })
  }

  /**
   * Initialize performance monitoring
   */
  const initPerformanceMonitoring = () => {
    if (!('PerformanceObserver' in window)) return

    // Monitor Core Web Vitals
    performanceObserver = new PerformanceObserver((entryList) => {
      entryList.getEntries().forEach((entry) => {
        switch (entry.entryType) {
          case 'paint':
            if (entry.name === 'first-contentful-paint') {
              performanceMetrics.value.firstContentfulPaint = entry.startTime
            }
            break
          case 'largest-contentful-paint':
            performanceMetrics.value.largestContentfulPaint = entry.startTime
            break
          case 'first-input':
            performanceMetrics.value.firstInputDelay = entry.processingStart - entry.startTime
            break
          case 'layout-shift':
            if (!entry.hadRecentInput) {
              performanceMetrics.value.cumulativeLayoutShift += entry.value
            }
            break
        }
      })
    })

    try {
      performanceObserver.observe({ entryTypes: ['paint', 'largest-contentful-paint', 'first-input', 'layout-shift'] })
    } catch (e) {
      // Some metrics might not be supported
      console.warn('Some performance metrics not supported:', e)
    }
  }

  /**
   * Measure load time
   */
  const measureLoadTime = () => {
    window.addEventListener('load', () => {
      const loadTime = performance.timing.loadEventEnd - performance.timing.navigationStart
      performanceMetrics.value.loadTime = loadTime
      
      // Log performance metrics in development
      if (import.meta.env.DEV) {
        // Performance metrics tracked internally
      }
    })
  }

  /**
   * Memory usage monitoring
   */
  const monitorMemoryUsage = () => {
    if (!performance.memory) return

    const checkMemory = () => {
      const memInfo = performance.memory
      const usedMemoryMB = Math.round(memInfo.usedJSHeapSize / 1024 / 1024)
      const totalMemoryMB = Math.round(memInfo.totalJSHeapSize / 1024 / 1024)
      
      // Warn if memory usage is high
      if (usedMemoryMB > 50) { // 50MB threshold
        console.warn('High memory usage detected:', {
          used: `${usedMemoryMB}MB`,
          total: `${totalMemoryMB}MB`,
          percentage: `${Math.round((usedMemoryMB / totalMemoryMB) * 100)}%`
        })
      }
    }

    // Check memory usage every 30 seconds in development
    if (import.meta.env.DEV) {
      setInterval(checkMemory, 30000)
    }
  }

  /**
   * Network-aware optimizations
   */
  const applyNetworkOptimizations = () => {
    const connection = navigator.connection
    if (!connection) return

    const isSlowConnection = ['slow-2g', '2g'].includes(connection.effectiveType)
    const isSaveData = connection.saveData

    if (isSlowConnection || isSaveData) {
      // Disable non-essential animations
      document.documentElement.classList.add('reduce-animations')
      
      // Skip non-critical image loading
      const nonCriticalImages = document.querySelectorAll('.lazy-image:not(.critical)')
      nonCriticalImages.forEach(img => {
        img.style.display = 'none'
      })
      
      // Reduce glassmorphism effects
      document.documentElement.classList.add('reduce-effects')
    }
  }

  /**
   * Bundle size monitoring
   */
  const monitorBundleSize = () => {
    if (!import.meta.env.DEV) return

    const navigationEntries = performance.getEntriesByType('navigation')
    if (navigationEntries.length > 0) {
      const entry = navigationEntries[0]
      const transferSize = entry.transferSize || 0
      const decodedBodySize = entry.decodedBodySize || 0
      
      // Bundle size info tracked internally
      
      // Warn if bundle is too large
      if (transferSize > 500 * 1024) { // 500KB threshold
        console.warn('Bundle size is large - consider code splitting')
      }
    }
  }

  /**
   * Cleanup function
   */
  const cleanup = () => {
    if (imageObserver) {
      imageObserver.disconnect()
      imageObserver = null
    }
    
    if (performanceObserver) {
      performanceObserver.disconnect()
      performanceObserver = null
    }
    
    lazyImages.value.clear()
    preloadedResources.value.clear()
  }

  // Initialize everything on mount
  onMounted(async () => {
    await nextTick() // Wait for DOM to be ready
    
    initLazyLoading()
    initPerformanceMonitoring()
    measureLoadTime()
    applyNetworkOptimizations()
    
    if (import.meta.env.DEV) {
      monitorMemoryUsage()
      monitorBundleSize()
    }
  })

  // Cleanup on unmount
  onUnmounted(() => {
    cleanup()
  })

  /**
   * Measure component performance metrics
   */
  const measurePerformance = (eventName, componentName) => {
    if (!performance || !performance.mark) return

    const startMark = `${componentName}-${eventName}-start`
    const endMark = `${componentName}-${eventName}-end`
    const measureName = `${componentName}-${eventName}`

    performance.mark(startMark)
    
    // Return a function to end the measurement
    return () => {
      try {
        // Verify start mark exists before measuring
        const startMarks = performance.getEntriesByName(startMark, 'mark')
        if (startMarks.length === 0) {
          return // Start mark doesn't exist, skip measurement silently
        }

        performance.mark(endMark)
        performance.measure(measureName, startMark, endMark)

        // Get the measurement
        const measure = performance.getEntriesByName(measureName)[0]
        if (measure && import.meta.env.DEV) {
          // Performance measure tracked internally
        }

        // Clean up marks
        performance.clearMarks(startMark)
        performance.clearMarks(endMark)
        performance.clearMeasures(measureName)
      } catch (e) {
        // Silently ignore performance measurement errors
        if (import.meta.env.DEV) {
          console.debug('Performance measurement skipped:', e.message)
        }
      }
    }
  }

  return {
    // Methods
    preloadCriticalResources,
    prefetchResources,
    observeLazyImages,
    loadImage,
    measurePerformance,
    
    // State
    performanceMetrics,
    lazyImages: lazyImages.value,
    preloadedResources: preloadedResources.value,
    
    // Utils
    cleanup
  }
}

/**
 * Utility to create optimized image element
 */
export function createOptimizedImage(src, options = {}) {
  const img = document.createElement('img')
  
  const {
    dataSrc = src,
    loading = 'lazy',
    alt = '',
    className = 'lazy-image',
    placeholder = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"%3E%3C/svg%3E'
  } = options
  
  img.dataset.src = dataSrc
  img.src = placeholder
  img.loading = loading
  img.alt = alt
  img.className = className + ' skeleton-optimized loading'
  
  return img
}

/**
 * Utility to check if user prefers reduced data usage
 */
export function prefersReducedData() {
  return navigator.connection?.saveData || 
         navigator.connection?.effectiveType === 'slow-2g' || 
         navigator.connection?.effectiveType === '2g'
}